// src/components/staff/ServicesChipSelect.tsx
import React from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";
import { ServiceSummary } from "../../api/staff";

type ServicesChipSelectProps = {
  services: ServiceSummary[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  loading?: boolean;
};

const ServicesChipSelect: React.FC<ServicesChipSelectProps> = ({
  services,
  selectedIds,
  onChange,
  loading,
}) => {
  // ⭐ Always ensure we have an array to map over
  const safeServices = Array.isArray(services) ? services : [];

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Services
      </Typography>

      {loading && safeServices.length === 0 ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading services...
          </Typography>
        </Box>
      ) : safeServices.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No services available. Configure services first.
        </Typography>
      ) : (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {safeServices.map((svc) => {
            const selected = selectedIds.includes(svc.service_id);
            return (
              <Chip
                key={svc.service_id}
                label={svc.service_name}
                color={selected ? "primary" : "default"}
                variant={selected ? "filled" : "outlined"}
                size="small"
                onClick={() => toggle(svc.service_id)}
              />
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default ServicesChipSelect;
